import { memo, useEffect, useMemo, useState } from 'react'
import { Play, Square } from 'lucide-react'

import { listVoices, type VoiceSummary, type VoiceProfileDict } from '@ponpon/api-client'
import { getTtsProviderLabel } from '../../../../constants/modelAliases'

const LABEL_CLASS = 'text-xxs uppercase tracking-wider text-text-muted block mb-0.5'
const FIELD_CLASS = 'rounded-lg bg-input-bg border border-input-border px-2 py-1 text-xs text-foreground placeholder:text-text-muted transition-colors hover:bg-surface-0 hover:border-input-border-hover focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'

export function getVoiceProviderLabel(provider: string): string {
  return getTtsProviderLabel(provider)
}

const VoiceProfileForm = memo(function VoiceProfileForm({
  initialProfile,
  provider,
  submitLabel,
  onSave,
  onCancel,
}: {
  initialProfile?: VoiceProfileDict
  provider: string
  submitLabel: string
  onSave: (profile: VoiceProfileDict) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initialProfile?.name ?? '')
  const [voiceId, setVoiceId] = useState(initialProfile?.voice_id ?? '')
  const [voiceName, setVoiceName] = useState(initialProfile?.voice_name ?? '')
  const [description, setDescription] = useState(initialProfile?.description ?? '')
  const [voices, setVoices] = useState<VoiceSummary[]>([])
  const [voiceLoadState, setVoiceLoadState] = useState<'loading' | 'loaded' | 'error'>('loading')
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null)
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)

  useEffect(() => {
    setVoiceLoadState('loading')
    setVoices([])
    listVoices(provider)
      .then((v) => { setVoices(v); setVoiceLoadState('loaded') })
      .catch(() => setVoiceLoadState('error'))
  }, [provider])

  useEffect(() => {
    return () => { previewAudio?.pause() }
  }, [previewAudio])

  const maleVoices = useMemo(() => voices.filter((v) => v.labels.gender === 'male'), [voices])
  const femaleVoices = useMemo(() => voices.filter((v) => v.labels.gender === 'female'), [voices])
  const otherVoices = useMemo(
    () => voices.filter((v) => v.labels.gender !== 'male' && v.labels.gender !== 'female'),
    [voices],
  )

  const selectedVoice = useMemo(() => voices.find((v) => v.voice_id === voiceId), [voices, voiceId])
  const canSave = Boolean(name.trim() && selectedVoice)

  useEffect(() => {
    if (!voiceId || voiceLoadState !== 'loaded' || selectedVoice) return
    setVoiceId('')
    setVoiceName('')
    setDescription('')
  }, [selectedVoice, voiceId, voiceLoadState])

  const handleVoiceSelect = (selectedId: string) => {
    setVoiceId(selectedId)
    const match = voices.find((v) => v.voice_id === selectedId)
    if (match) {
      setVoiceName(match.name)
      setDescription(match.description)
    }
  }

  const handlePreview = () => {
    if (!selectedVoice?.preview_url) return
    if (playingVoiceId === selectedVoice.voice_id) {
      previewAudio?.pause()
      setPlayingVoiceId(null)
      return
    }
    previewAudio?.pause()
    const audio = new Audio(selectedVoice.preview_url)
    audio.onended = () => setPlayingVoiceId(null)
    audio.play().catch(() => setPlayingVoiceId(null))
    setPreviewAudio(audio)
    setPlayingVoiceId(selectedVoice.voice_id)
  }

  const handleSave = () => {
    const trimmedName = name.trim()
    const trimmedVoiceId = voiceId.trim()
    if (!trimmedName || !trimmedVoiceId || !selectedVoice) return

    onSave({
      provider,
      name: trimmedName,
      voice_id: trimmedVoiceId,
      voice_name: voiceName.trim() || selectedVoice.name,
      description: description.trim(),
    })
  }

  return (
    <div className="mt-2 rounded-lg border border-input-border bg-surface-1 p-3 space-y-2" data-testid="voice-profile-form">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={LABEL_CLASS}>Key</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`h-7 w-full ${FIELD_CLASS}`}
            placeholder="Kai or opening_narrator"
            data-testid="voice-profile-name-input"
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>Voice</label>
          {voiceLoadState === 'loading' && (
            <div className={`h-7 w-full flex items-center px-2 text-xs text-text-muted ${FIELD_CLASS}`} data-testid="voice-profile-loading">
              Loading voices...
            </div>
          )}
          {voiceLoadState === 'error' && (
            <div className="space-y-1" data-testid="voice-profile-error">
              <div className="flex gap-1">
                <div className={`h-7 w-full flex items-center px-2 text-xs text-text-muted ${FIELD_CLASS}`}>
                  {getVoiceProviderLabel(provider)} voice catalog unavailable
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setVoiceLoadState('loading')
                    listVoices(provider)
                      .then((v) => { setVoices(v); setVoiceLoadState('loaded') })
                      .catch(() => setVoiceLoadState('error'))
                  }}
                  className="h-7 shrink-0 rounded-md border border-input-border bg-surface-0 px-2 text-xs text-text-muted transition-colors hover:bg-surface-3 hover:text-foreground"
                  data-testid="voice-retry-btn"
                >
                  Retry
                </button>
              </div>
              <p className="text-xxs text-destructive">Voice catalog unavailable; retry before saving</p>
            </div>
          )}
          {voiceLoadState === 'loaded' && (
            <div className="flex gap-1">
              <select
                value={voiceId}
                onChange={(e) => handleVoiceSelect(e.target.value)}
                className={`h-7 w-full min-w-0 ${FIELD_CLASS}`}
                data-testid="voice-profile-id-select"
              >
                <option value="">Select a {getVoiceProviderLabel(provider)} voice...</option>
                {femaleVoices.length > 0 && (
                  <optgroup label="Female">
                    {femaleVoices.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>
                        {v.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {maleVoices.length > 0 && (
                  <optgroup label="Male">
                    {maleVoices.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>
                        {v.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {otherVoices.length > 0 && (
                  <optgroup label="Other">
                    {otherVoices.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>
                        {v.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {selectedVoice?.preview_url && (
                <button
                  type="button"
                  onClick={handlePreview}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-input-border bg-surface-0 text-text-muted transition-colors hover:bg-surface-3 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                  title={playingVoiceId === selectedVoice.voice_id ? 'Stop preview' : 'Preview voice'}
                  aria-label={playingVoiceId === selectedVoice.voice_id ? 'Stop voice preview' : 'Preview voice'}
                  data-testid="voice-preview-btn"
                >
                  {playingVoiceId === selectedVoice.voice_id ? (
                    <Square className="h-3.5 w-3.5" strokeWidth={2} />
                  ) : (
                    <Play className="h-3.5 w-3.5" strokeWidth={2} />
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={LABEL_CLASS}>Voice name <span className="normal-case">(optional)</span></label>
          <input
            value={voiceName}
            onChange={(e) => setVoiceName(e.target.value)}
            className={`h-7 w-full ${FIELD_CLASS}`}
            placeholder="Display name"
            data-testid="voice-profile-voice-name-input"
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>Description <span className="normal-case">(optional)</span></label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`h-7 w-full ${FIELD_CLASS}`}
            placeholder="warm narrator"
            data-testid="voice-profile-description-input"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
          data-testid="voice-profile-cancel-btn"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="h-7 rounded-lg border border-primary/40 bg-primary/20 px-3 text-xs text-primary transition-colors hover:bg-primary/30 hover:border-primary/60 disabled:cursor-not-allowed disabled:border-input-border disabled:bg-surface-1 disabled:text-text-muted disabled:hover:bg-surface-1 disabled:hover:border-input-border focus:outline-none focus:ring-2 focus:ring-primary/20"
          data-testid="voice-profile-save-btn"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  )
})

export interface VoiceProfilesPanelProps {
  profiles: VoiceProfileDict[]
  suggestedRoleIds: string[]
  provider: string
  portLabel?: string
  readOnly?: boolean
  readOnlyMessage?: string
  onMakeLocalCopy?: () => void
  onAdd: (profile: VoiceProfileDict) => void
  onUpdate: (index: number, profile: VoiceProfileDict) => void
  onDelete: (index: number) => void
}

export const VoiceProfilesPanel = memo(function VoiceProfilesPanel({
  profiles,
  suggestedRoleIds,
  provider,
  portLabel = 'in_voice_profiles',
  readOnly = false,
  readOnlyMessage = 'Connected voice profiles are read-only. Change the source node voice profile, or make a local copy for this node.',
  onMakeLocalCopy,
  onAdd,
  onUpdate,
  onDelete,
}: VoiceProfilesPanelProps) {
  const [editingIndex, setEditingIndex] = useState<number | 'new' | null>(null)
  const [draftProfile, setDraftProfile] = useState<VoiceProfileDict | undefined>(undefined)
  const configuredKeys = useMemo(
    () => new Set(profiles.map((profile) => profile.name).filter((name): name is string => Boolean(name))),
    [profiles],
  )
  const missingRoleIds = suggestedRoleIds.filter((roleId) => !configuredKeys.has(roleId))

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-0 p-3" data-testid="voice-profiles-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-wider text-text-muted">Voice Profiles</label>
            <span className="rounded-full border border-input-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
              {portLabel}
            </span>
            <span className="rounded-full border border-input-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
              {getVoiceProviderLabel(provider)}
            </span>
            {readOnly && (
              <span className="rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-xxs text-primary">
                connected
              </span>
            )}
          </div>
        </div>
        {readOnly ? (
          onMakeLocalCopy ? (
            <button
              type="button"
              onClick={onMakeLocalCopy}
              className="h-7 shrink-0 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="make-local-voice-profiles-copy-btn"
            >
              Make local copy
            </button>
          ) : null
        ) : (
          <button
            type="button"
            onClick={() => {
              setDraftProfile(undefined)
              setEditingIndex('new')
            }}
            className="h-7 shrink-0 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
            data-testid="add-voice-profile-btn"
          >
            Add profile
          </button>
        )}
      </div>

      {readOnly && (
        <p className="mt-2 rounded-lg border border-primary/20 bg-primary/10 px-2 py-1 text-xxs text-primary/90" data-testid="voice-profile-connected-warning">
          {readOnlyMessage}
        </p>
      )}

      {!readOnly && missingRoleIds.length > 0 && editingIndex === null && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="missing-voice-role-list">
          {missingRoleIds.map((roleId) => (
            <button
              key={roleId}
              type="button"
              onClick={() => {
                setDraftProfile({
                  provider,
                  name: roleId,
                  voice_id: '',
                  voice_name: '',
                  description: '',
                })
                setEditingIndex('new')
              }}
              className="h-6 rounded-full border border-input-border bg-surface-1 px-2 text-xxs text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="add-missing-voice-role-btn"
            >
              Add {roleId}
            </button>
          ))}
        </div>
      )}

      {!readOnly && editingIndex === 'new' && (
        <VoiceProfileForm
          initialProfile={draftProfile}
          provider={provider}
          submitLabel="Save profile"
          onSave={(profile) => {
            onAdd(profile)
            setEditingIndex(null)
            setDraftProfile(undefined)
          }}
          onCancel={() => {
            setEditingIndex(null)
            setDraftProfile(undefined)
          }}
        />
      )}

      <div className="mt-3 space-y-2">
        {profiles.length > 0 ? (
          profiles.map((profile, i) => {
            if (!readOnly && editingIndex === i) {
              return (
                <VoiceProfileForm
                  key={`voice-profile-edit-${i}`}
                  initialProfile={profile}
                  provider={profile.provider || provider}
                  submitLabel="Save profile"
                  onSave={(updatedProfile) => {
                    onUpdate(i, updatedProfile)
                    setEditingIndex(null)
                  }}
                  onCancel={() => setEditingIndex(null)}
                />
              )
            }

            return (
              <div
                key={`${profile.name ?? profile.voice_id}-${i}`}
                className="rounded-lg border border-border-subtle bg-surface-1 p-2"
                data-testid="voice-profile-card"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-foreground/90">{profile.name ?? 'Unnamed'}</p>
                    <p className="mt-0.5 truncate text-xs text-text-muted">
                      {profile.voice_name || profile.voice_id} · {getVoiceProviderLabel(profile.provider || provider)}
                    </p>
                  </div>
                  {!readOnly && (
                    <div className="flex shrink-0 gap-1">
                      <button
                        type="button"
                        onClick={() => setEditingIndex(i)}
                        className="h-6 rounded-md border border-input-border bg-surface-0 px-1.5 text-xxs text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        data-testid="voice-profile-edit-btn"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          onDelete(i)
                          setEditingIndex(null)
                        }}
                        className="h-6 rounded-md border border-input-border bg-surface-0 px-1.5 text-xxs text-text-muted transition-colors hover:bg-destructive/10 hover:border-destructive/50 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-destructive/20"
                        data-testid="voice-profile-delete-btn"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })
        ) : (
          <p className="rounded-lg border border-border-subtle bg-surface-1 px-2 py-2 text-sm text-text-muted" data-testid="voice-profile-empty-state">
            No voice profiles on this clip.
          </p>
        )}
      </div>
    </div>
  )
})

VoiceProfileForm.displayName = 'VoiceProfileForm'
VoiceProfilesPanel.displayName = 'VoiceProfilesPanel'
